const kidsShowcaseClampCents = (value) => Math.max(0, Math.round(Number(value) || 0));

const kidsShowcaseCalculatePrices = ({
  unitPriceCents,
  allocationPriceCents,
  packSize,
  oneTimeDiscountCents = 0,
  subscriptionDiscountCents = 0,
}) => {
  const safePackSize = Math.min(3, Math.max(1, Number(packSize) || 1));
  const packMsrp = kidsShowcaseClampCents(unitPriceCents) * safePackSize;
  const subscriptionBase = kidsShowcaseClampCents(allocationPriceCents) * safePackSize;

  return {
    packMsrp,
    oneTimePrice: kidsShowcaseClampCents(packMsrp - oneTimeDiscountCents),
    subscriptionBase,
    subscriptionPrice: kidsShowcaseClampCents(subscriptionBase - subscriptionDiscountCents),
  };
};

const kidsShowcaseMergeDiscountCodes = (existingCodes, activeCode) => {
  const codes = [];
  const seen = new Set();

  for (const value of [...existingCodes, activeCode]) {
    const code = String(value || '').trim();
    const key = code.toLocaleLowerCase();
    if (!code || seen.has(key)) continue;
    seen.add(key);
    codes.push(code);
  }

  return codes;
};

const kidsShowcaseCartDiscountCodes = (cart) => {
  const applications = [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.discount_applications || []),
    ...(cart.items || []).flatMap((item) => {
      return (item.line_level_discount_allocations || [])
        .map((allocation) => allocation.discount_application)
        .filter(Boolean);
    }),
  ];

  return applications
    .filter((application) => application.type === 'discount_code')
    .map((application) => application.code || application.title)
    .filter(Boolean);
};

const kidsShowcaseActiveDiscountCode = ({
  promoCode,
  packConfig = {},
  packSize = 1,
  purchaseType = 'one-time',
}) => {
  if (Number(packSize) === 1) return String(promoCode || '').trim();

  const packCode = purchaseType === 'subscription'
    ? packConfig.subscriptionCode
    : packConfig.oneTimeCode;
  return String(packCode || '').trim();
};

const kidsShowcaseFormatPlanOptions = (options = []) => {
  return options.map((option) => {
    if (typeof option === 'string') return option;
    return option.value || option.name || '';
  }).filter(Boolean).join(' · ');
};

if (typeof module !== 'undefined') {
  module.exports = {
    activeDiscountCode: kidsShowcaseActiveDiscountCode,
    calculatePrices: kidsShowcaseCalculatePrices,
    mergeDiscountCodes: kidsShowcaseMergeDiscountCodes,
    cartDiscountCodes: kidsShowcaseCartDiscountCodes,
    formatPlanOptions: kidsShowcaseFormatPlanOptions,
  };
}

const KidsProductShowcaseBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class KidsProductShowcaseCard extends KidsProductShowcaseBase {
    constructor() {
      super();
      this.abortController = null;
      this.config = null;
      this.selectedVariantId = null;
      this.packSize = 1;
      this.purchaseType = 'subscription';
      this.isSubmitting = false;
      this.embla = null;
      this.thumbnailEmbla = null;
      this.selectedSellingPlanId = '';
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.initialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.embla?.destroy();
      this.thumbnailEmbla?.destroy();
      this.embla = null;
      this.thumbnailEmbla = null;
    }

    initialize() {
      const configNode = this.querySelector('[data-card-config]');
      this.form = this.querySelector('[data-product-form]');
      this.submitButton = this.querySelector('[data-submit]');
      this.errorNode = this.querySelector('[data-error]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.quantityInput = this.querySelector('[data-quantity]');
      this.sellingPlanInput = this.querySelector('[data-selling-plan]');
      this.gallery = this.querySelector('[data-product-gallery]');
      this.carouselViewport = this.querySelector('[data-carousel-viewport]');
      this.thumbnailViewport = this.querySelector('[data-thumbnail-viewport]');
      this.gallerySlides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.packButtons = [...this.querySelectorAll('[data-pack-size]')];
      this.variantButtons = [...this.querySelectorAll('[data-variant-option]')];
      this.thumbnailButtons = [...this.querySelectorAll('[data-thumbnail]')];
      this.purchaseInputs = [...this.querySelectorAll('[data-purchase-type]')];
      this.sellingPlanSelect = this.querySelector('[data-selling-plan-options]');
      this.mixedItems = [...this.querySelectorAll('[data-mixed-variant]')];

      if (!configNode || !this.form || !this.submitButton) return;

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        this.showError('This product is temporarily unavailable.');
        console.error('Kids showcase card configuration error', error);
        return;
      }

      this.selectedVariantId = Number(this.config.selectedVariantId);
      this.selectedSellingPlanId = this.sellingPlanSelect?.value || '';
      const signal = this.abortController.signal;

      this.initializeCarousel();

      for (const button of this.packButtons) {
        button.addEventListener('click', () => this.selectPack(Number(button.dataset.packSize)), { signal });
      }

      for (const button of this.variantButtons) {
        button.addEventListener('click', () => this.selectVariant(Number(button.dataset.variantId)), { signal });
      }

      for (const button of this.thumbnailButtons) {
        button.addEventListener('click', () => this.showImage(Number(button.dataset.imageId)), { signal });
      }

      for (const input of this.purchaseInputs) {
        input.addEventListener('change', () => this.selectPurchaseType(input.value), { signal });
      }

      this.sellingPlanSelect?.addEventListener('change', () => {
        this.selectedSellingPlanId = this.sellingPlanSelect.value;
        this.syncSellingPlanInput();
        this.updatePrices();
      }, { signal });

      for (const item of this.mixedItems) {
        for (const button of item.querySelectorAll('[data-quantity-change]')) {
          button.addEventListener('click', () => this.changeMixedQuantity(item, Number(button.dataset.quantityChange)), { signal });
        }
      }

      this.form.addEventListener('submit', (event) => this.submit(event), { signal });
      this.syncSubscriptionAvailability();
      this.selectPack(1);
      this.selectVariant(this.selectedVariantId);
    }

    initializeCarousel() {
      this.embla?.destroy();
      if (!this.carouselViewport || !window.EmblaCarousel) return;

      this.embla = window.EmblaCarousel(this.carouselViewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false,
        watchDrag: this.gallerySlides.length > 1,
      });
      if (this.thumbnailViewport) {
        this.thumbnailEmbla = window.EmblaCarousel(this.thumbnailViewport, {
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: true,
          slidesToScroll: 1,
        });
      }
      this.embla.on('select', () => this.syncGallerySelection(this.embla.selectedScrollSnap()));
      this.syncGallerySelection(this.embla.selectedScrollSnap());
    }

    get selectedVariant() {
      return this.config?.variants.find((variant) => Number(variant.id) === Number(this.selectedVariantId));
    }

    get packConfig() {
      return this.config?.packs[String(this.packSize)] || this.config?.packs['1'];
    }

    selectPack(packSize) {
      const nextPackSize = Math.min(3, Math.max(1, Number(packSize) || 1));
      if (!this.config.packs[String(nextPackSize)]) return;
      this.packSize = nextPackSize;
      this.quantityInput.value = String(nextPackSize);

      for (const button of this.packButtons) {
        const selected = Number(button.dataset.packSize) === nextPackSize;
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('active', selected);
        button.setAttribute('aria-pressed', String(selected));
      }

      const singleVariants = this.querySelector('[data-single-variants]');
      const mixedVariants = this.querySelector('[data-mixed-variants]');
      singleVariants.hidden = nextPackSize > 1;
      mixedVariants.hidden = nextPackSize === 1;

      for (const item of this.mixedItems) {
        const input = item.querySelector('[data-variant-quantity]');
        input.value = '0';
      }

      if (nextPackSize > 1) {
        const selectedItem = this.mixedItems.find((item) => Number(item.dataset.variantId) === this.selectedVariantId);
        const selectedInput = selectedItem?.querySelector('[data-variant-quantity]');
        if (selectedInput && this.variantCanSupply(this.selectedVariant, nextPackSize)) {
          selectedInput.value = String(nextPackSize);
        }
      }

      this.syncMixedControls();
      this.syncSubscriptionAvailability();
      this.updatePrices();
      this.updateSubmitState();
    }

    selectVariant(variantId) {
      const variant = this.config.variants.find((item) => Number(item.id) === Number(variantId));
      if (!variant) return;
      this.selectedVariantId = Number(variant.id);
      this.variantInput.value = String(variant.id);

      for (const button of this.variantButtons) {
        const selected = Number(button.dataset.variantId) === this.selectedVariantId;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      }

      if (variant.featuredImageId) this.showImage(Number(variant.featuredImageId));
      this.updateCallouts(variant.callouts || []);
      this.updateNutrition();
      this.syncSubscriptionAvailability();
      this.updatePrices();
      this.updateSubmitState();
    }

    selectPurchaseType(purchaseType) {
      this.purchaseType = purchaseType === 'subscription' && this.findSellingPlanForSelectedVariant()
        ? 'subscription'
        : 'one-time';

      for (const input of this.purchaseInputs) {
        const selected = input.value === this.purchaseType;
        input.checked = selected;
        const option = input.closest('[data-purchase-option]');
        option?.classList.toggle('is-selected', selected);
        option?.classList.toggle('rtnu-custom-radio--active', selected);
      }

      this.syncSellingPlanInput();
      this.updatePrices();
    }

    syncSubscriptionAvailability() {
      const subscriptionOption = this.querySelector('[data-purchase-option="subscription"]');
      const subscriptionInput = subscriptionOption?.querySelector('[data-purchase-type]');
      const hasSubscription = Boolean(this.findSellingPlanForSelectedVariant());

      if (subscriptionOption) subscriptionOption.hidden = !hasSubscription;
      if (subscriptionInput) subscriptionInput.disabled = !hasSubscription;
      if (!hasSubscription && this.purchaseType === 'subscription') this.purchaseType = 'one-time';
      this.selectPurchaseType(this.purchaseType);
    }

    findSellingPlanForSelectedVariant() {
      const variant = this.selectedVariant;
      if (!variant) return null;
      const allowedPlanIds = new Set(this.config.sellingPlans.map((plan) => String(plan.id)));
      const override = String(this.packConfig.sellingPlanId || this.selectedSellingPlanId || '').trim();

      return variant.allocations.find((allocation) => {
        if (!allowedPlanIds.has(String(allocation.planId))) return false;
        return override ? String(allocation.planId) === override : true;
      }) || null;
    }

    syncSellingPlanInput() {
      const allocation = this.purchaseType === 'subscription' ? this.findSellingPlanForSelectedVariant() : null;
      this.sellingPlanInput.value = allocation ? String(allocation.planId) : '';
      this.sellingPlanInput.disabled = !allocation;

      const plan = allocation
        ? this.config.sellingPlans.find((item) => String(item.id) === String(allocation.planId))
        : null;
      if (this.sellingPlanSelect && allocation) this.sellingPlanSelect.value = String(allocation.planId);
    }

    changeMixedQuantity(item, change) {
      const input = item.querySelector('[data-variant-quantity]');
      const current = Number(input.value) || 0;
      const total = this.mixedQuantityTotal();
      const next = Math.max(0, current + change);
      if (change > 0 && total >= this.packSize) return;
      input.value = String(next);
      this.syncMixedControls();
      this.updateSubmitState();
    }

    mixedQuantityTotal() {
      return this.mixedItems.reduce((total, item) => {
        return total + (Number(item.querySelector('[data-variant-quantity]')?.value) || 0);
      }, 0);
    }

    variantCanSupply(variant, quantity) {
      if (!variant?.available) return false;
      if (variant.inventoryPolicy !== 'deny') return true;
      return Number(variant.inventoryQuantity) >= Number(quantity);
    }

    syncMixedControls() {
      const total = this.mixedQuantityTotal();
      for (const item of this.mixedItems) {
        const input = item.querySelector('[data-variant-quantity]');
        const minus = item.querySelector('[data-quantity-change="-1"]');
        const plus = item.querySelector('[data-quantity-change="1"]');
        const variant = this.config.variants.find((entry) => Number(entry.id) === Number(item.dataset.variantId));
        const quantity = Number(input.value) || 0;
        minus.disabled = quantity === 0;
        plus.disabled = !this.variantCanSupply(variant, quantity + 1) || total >= this.packSize;
      }

      const validation = this.querySelector('[data-pack-validation]');
      if (validation) {
        validation.textContent = this.packSize > 1 && total !== this.packSize
          ? `Choose ${this.packSize - total} more.`
          : '';
      }
    }

    showImage(imageId) {
      const index = this.gallerySlides.findIndex((slide) => Number(slide.dataset.imageId) === Number(imageId));
      if (index < 0) return;
      if (this.embla) this.embla.scrollTo(index);
      this.syncGallerySelection(index);
    }

    syncGallerySelection(index) {
      const activeSlide = this.gallerySlides[index];
      if (!activeSlide) return;

      for (const slide of this.gallerySlides) {
        slide.classList.toggle('is-active', slide === activeSlide);
      }
      for (const button of this.thumbnailButtons) {
        const selected = Number(button.dataset.imageId) === Number(activeSlide.dataset.imageId);
        button.classList.toggle('is-active', selected);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      }
      this.thumbnailEmbla?.scrollTo(index);
    }

    updateCallouts(callouts) {
      for (const element of this.querySelectorAll('[data-callout]')) {
        const callout = callouts[Number(element.dataset.callout) - 1] || {};
        const heading = element.querySelector('[data-callout-heading]');
        const description = element.querySelector('[data-callout-description]');
        heading.textContent = `${callout.heading || ''}${callout.small || ''}`;
        description.textContent = callout.description || '';
        element.classList.toggle('is-hidden', !callout.heading);
      }
    }

    updateNutrition() {
      for (const link of this.querySelectorAll('[data-variant-nutrition]')) {
        link.hidden = Number(link.dataset.variantNutrition) !== this.selectedVariantId;
      }
    }

    updatePrices() {
      const variant = this.selectedVariant;
      if (!variant) return;
      const allocation = this.findSellingPlanForSelectedVariant();
      const prices = kidsShowcaseCalculatePrices({
        unitPriceCents: variant.price,
        allocationPriceCents: allocation?.price ?? variant.price,
        packSize: this.packSize,
        oneTimeDiscountCents: this.packConfig.oneTimeDiscount,
        subscriptionDiscountCents: this.packConfig.subscriptionDiscount,
      });

      this.setPrice('[data-one-time-price]', prices.oneTimePrice);
      this.setPrice('[data-one-time-msrp]', prices.packMsrp, prices.oneTimePrice < prices.packMsrp);
      this.setPrice('[data-subscription-price]', prices.subscriptionPrice);
      this.setPrice('[data-subscription-msrp]', prices.packMsrp, prices.subscriptionPrice < prices.packMsrp);
      this.setPrice('[data-subscription-savings]', prices.packMsrp - prices.subscriptionPrice);

      for (const savingsNode of this.querySelectorAll('[data-pack-savings]')) {
        const pack = this.config.packs[savingsNode.dataset.packSavings];
        const discount = this.purchaseType === 'subscription'
          ? pack?.subscriptionDiscount
          : pack?.oneTimeDiscount;
        savingsNode.textContent = discount > 0 ? `Save ${this.formatMoney(discount)}` : '';
      }
      this.syncSellingPlanInput();
    }

    setPrice(selector, cents, visible = true) {
      const node = this.querySelector(selector);
      if (!node) return;
      node.textContent = visible ? this.formatMoney(cents) : '';
      node.hidden = !visible;
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.config.currency,
        }).format(kidsShowcaseClampCents(cents) / 100);
      } catch (_error) {
        return `$${(kidsShowcaseClampCents(cents) / 100).toFixed(2)}`;
      }
    }

    updateSubmitState() {
      const variant = this.selectedVariant;
      const complete = this.packSize === 1 || this.mixedQuantityTotal() === this.packSize;
      const available = this.packSize === 1
        ? Boolean(variant?.available)
        : this.selectedMixedItems().every((item) => this.variantCanSupply(item.variant, item.quantity));
      const disabled = this.isSubmitting || !complete || !available;

      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute('aria-disabled', String(disabled));
      if (!this.isSubmitting) {
        this.submitButton.textContent = available
          ? this.submitButton.dataset.defaultLabel
          : this.submitButton.dataset.soldOutLabel;
      }
    }

    selectedMixedItems() {
      return this.mixedItems.flatMap((item) => {
        const quantity = Number(item.querySelector('[data-variant-quantity]')?.value) || 0;
        const variant = this.config.variants.find((entry) => Number(entry.id) === Number(item.dataset.variantId));
        return quantity > 0 && variant ? [{ variant, quantity }] : [];
      });
    }

    buildItems() {
      const selected = this.packSize === 1
        ? [{ variant: this.selectedVariant, quantity: 1 }]
        : this.selectedMixedItems();
      const sellingPlan = this.purchaseType === 'subscription' ? this.findSellingPlanForSelectedVariant() : null;

      return selected.map(({ variant, quantity }) => {
        const item = {
          id: variant.id,
          quantity,
          properties: {
            _kids_showcase_product: this.config.productTitle,
            _kids_showcase_card: this.config.cardKey,
            _kids_showcase_pack: String(this.packSize),
          },
        };
        if (sellingPlan) item.selling_plan = sellingPlan.planId;
        return item;
      });
    }

    async submit(event) {
      event.preventDefault();
      if (this.isSubmitting || this.submitButton.disabled) return;
      const items = this.buildItems();
      if (!items.length || (this.packSize > 1 && this.mixedQuantityTotal() !== this.packSize)) {
        this.showError(`Choose exactly ${this.packSize} flavors before adding to cart.`);
        return;
      }

      this.hideError();
      this.isSubmitting = true;
      this.submitButton.textContent = 'Adding to cart…';
      this.updateSubmitState();
      const root = window.Shopify?.routes?.root || '/';

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
          signal: this.abortController.signal,
        });
        if (!addResponse.ok) throw await this.cartError(addResponse);

        const activeCode = kidsShowcaseActiveDiscountCode({
          promoCode: this.config.promoCode,
          packConfig: this.packConfig,
          packSize: this.packSize,
          purchaseType: this.purchaseType,
        });
        if (String(activeCode || '').trim()) await this.preserveDiscountCodes(root, activeCode);

        if (window.Rebuy?.SmartCart) {
          window.Rebuy.SmartCart.show();
        } else {
          window.location.assign(`${root}cart`);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Kids showcase cart error for ${this.config.cardKey}`, error);
          this.showError(error.message || 'There was an error adding this product. Please try again.');
        }
      } finally {
        this.isSubmitting = false;
        this.updateSubmitState();
      }
    }

    async preserveDiscountCodes(root, activeCode) {
      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });
      if (!cartResponse.ok) throw new Error('Could not read current cart discounts.');
      const cart = await cartResponse.json();
      const codes = kidsShowcaseMergeDiscountCodes(kidsShowcaseCartDiscountCodes(cart), activeCode);
      if (!codes.length) return;

      const updateResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
        signal: this.abortController.signal,
      });
      if (!updateResponse.ok) throw new Error('The bundle was added, but its discount could not be applied.');
    }

    async cartError(response) {
      try {
        const body = await response.json();
        return new Error(body.description || body.message || 'This product could not be added.');
      } catch (_error) {
        return new Error('This product could not be added.');
      }
    }

    showError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message;
      this.errorNode.hidden = false;
    }

    hideError() {
      if (!this.errorNode) return;
      this.errorNode.textContent = '';
      this.errorNode.hidden = true;
    }
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('kids-product-showcase-card')) {
    customElements.define('kids-product-showcase-card', KidsProductShowcaseCard);
  }
}

if (typeof module !== 'undefined') {
  module.exports.KidsProductShowcaseCard = KidsProductShowcaseCard;
}
